import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Phone,
  MapPin,
  Activity,
  Save,
  Loader2,
  FileText
} from 'lucide-react';
import styles from './CustomerDrawer.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';

const CustomerDrawer = ({ isOpen, onClose, onSave, customer = null }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    mobile: '',
    address: '',
    gst: '',
  });

  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (customer && customer.id) {
        fetchCustomerDetails(customer.id);
      } else {
        setFormData({
          customer_name: '',
          mobile: '',
          address: '',
          gst: '',
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customer]);

  const fetchCustomerDetails = async (id) => {
    setIsFetching(true);
    try {
      const response = await api.get(`/customers/${id}`);
      if (response.data.success) {
        const cust = response.data.data;
        setFormData({
          customer_name: cust.customer_name || '',
          mobile: cust.mobile || '',
          address: cust.address || '',
          gst: cust.gst || '',
        });
      }
    } catch (error) {
      console.error('Error fetching customer details:', error);
      toast.error('Failed to load customer details');
    } finally {
      setIsFetching(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (customer) {
        response = await api.put(`/customers/${customer.id}`, formData);
      } else {
        response = await api.post('/customers', formData);
      }

      if (response.data.success) {
        toast.success(customer ? 'Customer updated successfully' : 'Customer added successfully');
        onSave();
        onClose();
      }
    } catch (error) {
      console.error('Error saving customer:', error);
      const message = error.response?.data?.message || 'Failed to save customer';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{customer ? 'Edit Customer' : 'Add Customer'}</h2>
            <p>Fill in the client contact details</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          {isFetching ? (
            <div className={styles['fetching-state']}>
              <Loader2 size={40} className={styles['spinner']} />
              <p>Fetching customer details...</p>
            </div>
          ) : (
            <>
              <div className={styles['form-scroll-area']}>
                {/* Customer Name */}
                <div className={styles['form-group']}>
                  <label><User size={16} /> Customer Name <span className={styles['required-star']}>*</span></label>
                  <input 
                    type="text" 
                    name="customer_name"
                    value={formData.customer_name}
                    onChange={handleChange}
                    placeholder="Enter customer name"
                    required
                  />
                </div>

                {/* Mobile */}
                <div className={styles['form-group']}>
                  <label><Phone size={16} /> Mobile Number <span className={styles['required-star']}>*</span></label>
                  <input 
                    type="text" 
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                    required
                  />
                </div>

                {/* Address */}
                <div className={`${styles['form-group']} ${styles['full-width']}`}>
                  <label><MapPin size={16} /> Address</label>
                  <textarea 
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Enter full address"
                    rows={3}
                  />
                </div>

                {/* GST */}
                <div className={`${styles['form-group']} ${styles['full-width']}`}>
                  <label><FileText size={16} /> GST Number</label>
                  <input 
                    type="text" 
                    name="gst"
                    value={formData.gst}
                    onChange={handleChange}
                    placeholder="Enter GST number"
                  />
                </div>
              </div>

              <div className={styles['drawer-footer']}>
                <button type="button" className={styles['cancel-btn']} onClick={onClose}>
                  Cancel
                </button>
                <button type="submit" className={styles['save-btn']} disabled={loading}>
                  {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
                  <span>{customer ? 'Update Customer' : 'Save Customer'}</span>
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default CustomerDrawer;
